import { useState } from 'react'
import { useGameStore, Item } from '@store/gameStore'
import { GameLayout } from '@components/GameLayout'
import { getVIPIcon } from '@utils/vipHelpers'
import './HeroScreen.css'

// Equipment slots definition based on User Request
const EQUIPMENT_SLOTS = [
    { id: 'head', name: 'Kopf', emoji: '🪖' },
    { id: 'cape', name: 'Umhang', emoji: '🧣' },
    { id: 'weapon', name: 'Waffe', emoji: '⚔️' },
    { id: 'armor', name: 'Rüstung', emoji: '🛡️' },
    { id: 'shoes', name: 'Schuhe', emoji: '👞' },
    { id: 'ring', name: 'Ring', emoji: '💍' },
]

const HERO_IMAGES: { [key: string]: string } = {
    'default': '/rookie.png',
    'knight': '/ritter.png',
    'king': '/king.png',
    'wizard': '/magier.png'
}

const RARITY_COLORS: Record<string, string> = {
    common: '#9ca3af',
    uncommon: '#22c55e',
    rare: '#3b82f6',
    epic: '#a855f7',
    legendary: '#f59e0b',
}

export function HeroScreen() {
    const [selectedSlot, setSelectedSlot] = useState<string | null>(null)

    // Game Store
    const username = useGameStore(state => state.username)
    const heroSkin = useGameStore(state => state.heroSkin)
    const power = useGameStore(state => state.power)
    const playerLevel = useGameStore(state => state.playerLevel)
    const playerXP = useGameStore(state => state.playerXP)
    const xpToNextLevel = useGameStore(state => state.xpToNextLevel)
    const stats = useGameStore(state => state.stats)
    const statPoints = useGameStore(state => state.statPoints)
    const distributeStatPoint = useGameStore(state => state.distributeStatPoint)
    const isVIP = useGameStore(state => state.isVIP)
    const vipLevel = useGameStore(state => state.vipLevel)
    const vipExpiresAtGameMinute = useGameStore(state => state.vipExpiresAtGameMinute)
    const totalGameMinutes = useGameStore(state => state.totalGameMinutes)

    const inventory = useGameStore(state => state.inventory) || []
    const equipment = useGameStore(state => state.equipment) || {}

    const equipItem = useGameStore(state => state.equipItem)
    const setScreen = useGameStore(state => state.setScreen)

    // Tutorial integration
    const tutorialPhase = useGameStore(state => state.tutorialPhase)
    const tutorialSubStep = useGameStore(state => state.tutorialSubStep)
    const setTutorialPhase = useGameStore(state => state.setTutorialPhase)
    const setTutorialSubStep = useGameStore(state => state.setTutorialSubStep)
    const tutorialWonItem = useGameStore(state => state.tutorialWonItem)

    const [isVIPModalOpen, setIsVIPModalOpen] = useState(false)

    // Calculate equipment power bonus
    const equipmentPower = Object.values(equipment).reduce(
        (sum, item) => sum + (item?.power || 0),
        0
    )

    const statPower = (stats.atk + stats.def + stats.spd) * 5
    const totalPower = power + equipmentPower + statPower

    // Get items for a specific slot that are in inventory
    const getItemsForSlot = (slotId: string) => {
        return inventory.filter(item => item.slot === slotId)
    }

    // Equip an item
    const handleEquip = (item: Item) => {
        equipItem(item)
        setSelectedSlot(null)

        // Tutorial: Advance to battle phase after equipping
        if (tutorialPhase === 'hero') {
            setTimeout(() => {
                setTutorialPhase('battle')
                setScreen('battle')
            }, 500)
        }
    }

    const xpPercentage = Math.min(100, (playerXP / xpToNextLevel) * 100)

    // VIP duration calculation
    const remainingVIPMinutes = vipExpiresAtGameMinute ? Math.max(0, vipExpiresAtGameMinute - totalGameMinutes) : 0
    const vipDays = Math.floor(remainingVIPMinutes / (24 * 60))
    const vipHours = Math.floor((remainingVIPMinutes % (24 * 60)) / 60)

    return (
        <GameLayout noPadding noScroll showHeader={true} showNav={true}>
            <div className="hero">
                {/* BACKGROUND & OVERLAY */}
                <div className="hero__bg" style={{ backgroundImage: "url('/bg-char.jpg')" }} />

                <div className="hero__content">
                    {/* TOP HEADER INFO */}
                    <div className="hero__header">
                        <div className="hero__header-text">
                            <h1 className="hero__char-name">{username}</h1>
                            <div className="hero__char-level-group">
                                <span className="hero__char-level">Lvl {playerLevel}</span>
                                <div className="hero__xp-container">
                                    <div className="hero__xp-bar" style={{ width: `${xpPercentage}%` }} />
                                    <span className="hero__xp-text">{playerXP} / {xpToNextLevel} XP</span>
                                </div>
                            </div>
                        </div>

                        <div
                            className={`hero__vip-badge ${!isVIP ? 'hero__vip-badge--inactive' : ''}`}
                            onClick={() => setIsVIPModalOpen(true)}
                        >
                            <span className="hero__vip-icon">👑</span>
                            <span className="hero__vip-label">{isVIP ? `VIP ${vipLevel}` : 'VIP'}</span>
                        </div>
                    </div>

                    <div className="hero__main-grid">
                        {/* LEFT COLUMN: Equipment + Stats */}
                        <div className="hero__left-column">
                            {/* EQUIPMENT GRID (3x2) */}
                            <div className="hero__equipment-grid">
                                {EQUIPMENT_SLOTS.map(slot => {
                                    const equipped = equipment[slot.id]
                                    return (
                                        <div
                                            key={slot.id}
                                            id={tutorialPhase === 'hero' && slot.id === (tutorialWonItem?.slot || 'weapon') ? 'tutorial-equip-slot' : undefined}
                                            className={`hero__item-box ${equipped ? 'hero__item-box--equipped' : 'hero__item-box--empty'}`}
                                            onClick={() => {
                                                setSelectedSlot(slot.id)
                                                if (tutorialPhase === 'hero' && tutorialSubStep === 0) {
                                                    setTutorialSubStep(1)
                                                }
                                            }}
                                            style={equipped ? { borderColor: RARITY_COLORS[equipped.rarity] } : {}}
                                        >
                                            <div className="hero__item-label">{slot.name}</div>
                                            <div className="hero__item-icon">
                                                {equipped ? equipped.emoji : ''}
                                            </div>
                                            {equipped && (
                                                <>
                                                    <div className="hero__item-level">Lv.15</div>
                                                    <div className="hero__item-rarity" style={{ backgroundColor: RARITY_COLORS[equipped.rarity] }} />
                                                </>
                                            )}
                                            {!equipped && <div className="hero__item-placeholder">{slot.emoji}</div>}
                                        </div>
                                    )
                                })}
                            </div>

                            {/* STATS PANEL (now in flow) */}
                            <div className="hero__footer-actions">
                                <div className="hero__stats-panel">
                                    {statPoints > 0 && (
                                        <div className="hero__points-available">
                                            Noch {statPoints} Punkte
                                        </div>
                                    )}
                                    <div className="hero__stats-grid">
                                        <div className="hero__stat-row">
                                            <span className="hero__stat-icon">⚔️</span>
                                            <span className="hero__stat-label">ATK</span>
                                            <span className="hero__stat-val">{stats.atk}</span>
                                            {statPoints > 0 && (
                                                <button className="hero__stat-add" onClick={() => distributeStatPoint('atk')}>+</button>
                                            )}
                                        </div>
                                        <div className="hero__stats-divider" />
                                        <div className="hero__stat-row">
                                            <span className="hero__stat-icon">🛡️</span>
                                            <span className="hero__stat-label">DEF</span>
                                            <span className="hero__stat-val">{stats.def}</span>
                                            {statPoints > 0 && (
                                                <button className="hero__stat-add" onClick={() => distributeStatPoint('def')}>+</button>
                                            )}
                                        </div>
                                        <div className="hero__stats-divider" />
                                        <div className="hero__stat-row">
                                            <span className="hero__stat-icon">⚡</span>
                                            <span className="hero__stat-label">SPD</span>
                                            <span className="hero__stat-val">{stats.spd}</span>
                                            {statPoints > 0 && (
                                                <button className="hero__stat-add" onClick={() => distributeStatPoint('spd')}>+</button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT: Character Preview with Power Overlay */}
                        <div className="hero__char-preview">
                            <div className="hero__char-img-container">
                                <img
                                    src={HERO_IMAGES[heroSkin] || HERO_IMAGES.default}
                                    alt="Held"
                                    className="hero__char-img"
                                />

                                {/* KAMPFKRAFT OVERLAY */}
                                <div className="hero__power-overlay">
                                    <div className="hero__power-label">KAMPFKRAFT</div>
                                    <div className="hero__power-value">{totalPower.toLocaleString()}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* VIP STATUS MODAL */}
                {isVIPModalOpen && (
                    <div className="hero__modal-overlay" onClick={() => setIsVIPModalOpen(false)}>
                        <div className="hero__modal hero__modal--vip" onClick={e => e.stopPropagation()}>
                            <div className="hero__modal-header">
                                <h2>VIP STATUS</h2>
                                <button className="hero__modal-close" onClick={() => setIsVIPModalOpen(false)}>✕</button>
                            </div>
                            <div className="hero__vip-modal-content">
                                <div className={`hero__vip-status-icon ${!isVIP ? 'inactive' : ''}`}>{getVIPIcon(vipLevel, isVIP)}</div>

                                {isVIP ? (
                                    <>
                                        <div className="hero__vip-status-title">Aktiver VIP</div>
                                        <p className="hero__vip-desc">Du genießt alle exklusiven Vorteile!</p>
                                        <div className="hero__vip-timer">
                                            Restlaufzeit: <span>{vipDays}T {vipHours}Std</span>
                                        </div>
                                        <button
                                            className="btn btn--gold btn--sm w-full mt-2"
                                            style={{ marginTop: '10px' }}
                                            onClick={() => {
                                                useGameStore.getState().setShopTab('passes')
                                                setScreen('shop')
                                            }}
                                        >
                                            👑 VIP VERLÄNGERN
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <div className="hero__vip-status-title inactive">VIP Inaktiv</div>
                                        <p className="hero__vip-desc">Werde VIP für XP-Boosts, tägliche Belohnungen und mehr!</p>
                                        <button
                                            className="hero__vip-shop-btn"
                                            onClick={() => setScreen('shop')}
                                        >
                                            ZUM SHOP 🛒
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* SLOT SELECTION MODAL */}
                {selectedSlot && (
                    <div className="hero__modal-overlay" onClick={() => setSelectedSlot(null)}>
                        <div className="hero__modal" onClick={e => e.stopPropagation()}>
                            <div className="hero__modal-header">
                                <h2>{EQUIPMENT_SLOTS.find(s => s.id === selectedSlot)?.name} wählen</h2>
                                <button className="hero__modal-close" onClick={() => {
                                    setSelectedSlot(null)
                                    if (tutorialPhase === 'hero') {
                                        setTutorialSubStep(0)
                                    }
                                }}>✕</button>
                            </div>

                            <div className="hero__item-list">
                                {getItemsForSlot(selectedSlot).length > 0 ? (
                                    getItemsForSlot(selectedSlot).map(item => (
                                        <div
                                            key={item.id}
                                            className="hero__inv-item"
                                            style={{ borderColor: RARITY_COLORS[item.rarity] }}
                                            onClick={() => handleEquip(item)}
                                        >
                                            <span className="hero__inv-emoji">{item.emoji}</span>
                                            <div className="hero__inv-info">
                                                <span className="hero__inv-name" style={{ color: RARITY_COLORS[item.rarity] }}>
                                                    {item.name}
                                                </span>
                                                <span className="hero__inv-power">+{item.power} KAMPFKRAFT</span>
                                            </div>
                                            <button
                                                id={tutorialPhase === 'hero' && item.id === tutorialWonItem?.id ? 'tutorial-inv-item-btn' : undefined}
                                                className="hero__inv-btn"
                                            >
                                                ANLEGEN
                                            </button>
                                        </div>
                                    ))
                                ) : (
                                    <div className="hero__empty-inv">
                                        <div className="hero__empty-emoji">📦</div>
                                        <p>Du hast keine Ausrüstung für diesen Slot.</p>
                                        <button className="btn btn--gem" onClick={() => setScreen('gacha')}>
                                            Lootboxen öffnen
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </GameLayout>
    )
}
